import React, { useEffect, useState } from "react";
import { Box, Button, Tabs, Tab } from "@mui/material";
import HealthAreaTable from "./HealthAreaTable";
import DiseaseBurdensTable from "./DiseaseBurdensTable";
import { useAppSelector } from "../../../store/reduxHooks";
import {
  handleGetDiseaseBurdens,
  handleGetHealthAreaCategories,
  handlePublishHealthAreaCategory,
  IDiseaseItem,
  IHealthAreaItem,
} from "../../../api/mosaic/health_area";
import Base3StepModal from "./Base3StepModal";
import EditHealthCategoryModal from "./EditHealthCategoryModal";
import EditDiseaseModal from "./EditDiseaseModal";
import { addButtonSx } from "../../shared/buttonStyles";
import PageHeader from "../../shared/PageHeader";
import PageCard from "../../shared/PageCard";
import { useToast } from "../../shared/Toast";
import theme from "../../../utils/theme";

const HealthAreaComponent: React.FC = () => {
  const user = useAppSelector((state) => state.userSlice.user);
  const { showError } = useToast();

  const [selectedTab, setSelectedTab] = useState(0);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [healthAreaToEdit, setHealthAreaToEdit] = useState<IHealthAreaItem>();
  const [diseaseToEdit, setDiseaseToEdit] = useState<IDiseaseItem>();
  const [healthAreas, setHealthAreas] = useState<IHealthAreaItem[]>([]);
  const [diseaseBurdens, setDiseaseBurdens] = useState<IDiseaseItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const fetchHealthAreaData = async () => {
    try {
      setIsLoadingData(true);
      const [categories, diseases] = await Promise.all([
        handleGetHealthAreaCategories(),
        handleGetDiseaseBurdens(),
      ]);
      setHealthAreas(categories.data.data);
      setDiseaseBurdens(diseases.data.data);
      setIsLoadingData(false);
    } catch (err) {
      setIsLoadingData(false);
      showError("Error fetching health area data");
      console.error(err);
    }
  };

  useEffect(() => {
    if (user) fetchHealthAreaData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleTogglePublish = (id: number) => {
    handlePublishHealthAreaCategory(id)
      .then(() => fetchHealthAreaData())
      .catch((error) => {
        showError("Error toggling publish status");
        console.error(error);
      });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageHeader
        title="Health Areas"
        subtitle="Manage health area categories and disease burdens"
      >
        <Button variant="contained" onClick={() => setIsOpen(true)} sx={addButtonSx}>
          Add new
        </Button>
      </PageHeader>

      <Tabs
        value={selectedTab}
        onChange={(_e, val) => setSelectedTab(val)}
        sx={{
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 500, fontSize: "15px" },
          "& .Mui-selected": { color: "#28B2AC !important" },
          "& .MuiTabs-indicator": { backgroundColor: theme.palette.brand.primary },
        }}
      >
        <Tab label="Health Area Categories" />
        <Tab label="Disease Burdens" />
      </Tabs>

      <PageCard>
        {selectedTab === 0 ? (
          <HealthAreaTable
            isLoading={isLoadingData}
            tableData={healthAreas}
            handleEdit={(ha) => setHealthAreaToEdit(ha)}
            handleTogglePublish={handleTogglePublish}
          />
        ) : (
          <DiseaseBurdensTable
            isLoading={isLoadingData}
            diseasesData={diseaseBurdens}
            healthAreas={healthAreas}
            handleEdit={(d) => setDiseaseToEdit(d)}
          />
        )}
      </PageCard>

      <EditHealthCategoryModal
        healthAreaToEdit={healthAreaToEdit}
        isOpen={!!healthAreaToEdit}
        handleClose={() => setHealthAreaToEdit(undefined)}
        onUpdate={fetchHealthAreaData}
      />

      <EditDiseaseModal
        healthAreas={healthAreas}
        diseaseToEdit={diseaseToEdit}
        isOpen={!!diseaseToEdit}
        handleClose={() => setDiseaseToEdit(undefined)}
        onUpdate={fetchHealthAreaData}
      />

      <Base3StepModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
      />
    </Box>
  );
};

export default HealthAreaComponent;
